import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Form, theme } from "antd";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useOne,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import { useSession } from "hooks/useSession";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import { isLtiExperience } from "utils/isLtiExperience";
import { getShareAccess } from "utils/getShareAccess";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import { getPublicationUrl } from "pages/media/utils";
import type {
  CallToAction,
  MediaAsset,
  MediaProjectInstanceResponse,
  MediaProjectResponse,
  ProjectInstanceSettingsRequest,
} from "pages/media/types";
import type { ProjectAddOnsResponse } from "../../data/addonApi";
import type {
  ExperienceDetailsBlurField,
  ExperienceDetailsHiddenField,
} from "./general-settings/ExperienceDetailsSection";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { FormLabel } from "../FormLabel";
import { CONFIG_ITEM } from "../../data/addons";
import type { ExperienceLink, GeneralSettingsFormValues } from "./types";
import { GENERAL_SETTINGS_DEFAULTS } from "./defaults";
import { AccessSharingSection } from "./general-settings/AccessSharingSection";
import { ExperienceDetailsSection } from "./general-settings/ExperienceDetailsSection";
import { HeaderLinksSection } from "./general-settings/HeaderLinksSection";
import { LayoutSection } from "./general-settings/LayoutSection";
import { SlugSection } from "./general-settings/SlugSection";
import {
  hasLinkContent,
  getUnmanagedCallToActions,
  mapCallToActionsToLinks,
  mapLinksToCallToActions,
  normalizeLinkValue,
} from "./general-settings/headerLinksMapping";
import {
  SectionDivider,
  FieldBlock,
} from "./general-settings/GeneralSettingsConfigCard.styles";

const KEY = "createExperience.generalSettings";

// Generation fields the edit card doesn't render (MED-7303). Their values are
// still hydrated and sent back unchanged on save; to show them again, empty
// this list.
const HIDDEN_DETAILS_FIELDS: readonly ExperienceDetailsHiddenField[] = [
  "generation_prompt",
  "objectives",
  "language",
];

export interface GeneralSettingsEditCardProps {
  /** Whether the AI Chat add-on is enabled — drives the layout preview's
   *  read-only chat indicator (`ExperienceEditPage` reads this from the same
   *  add-ons list the rail uses, there's no PlaybookDraft on this page). */
  hasChatAddon: boolean;
}

/**
 * General card for the experience edit page (MED-6958).
 *
 * Unlike the create-flow `GeneralSettingsConfigCard` (which reads/writes the
 * PlaybookDraft context), this fetches the real project + instance and edits
 * them directly — there's no draft to reconcile. Shares the Experience
 * details and Header links sections with the create card so both render the
 * exact same fields. Layout stays inline per card (same options in both —
 * Sidebar position is Left/Right only, no "disappear" option anywhere), and
 * — like the create card — it isn't persisted anywhere yet.
 *
 * Title/description and the generation prompt save on blur (only if changed —
 * see `handleDetailsFieldBlur`); tags/language/links and the other generation
 * selects (goal, voice, brand kit, side panel) save immediately on change,
 * since none of those are driven by continuous typing. The generation fields
 * + side panel commit as the General add-on settings
 * (`commitGeneralSettings`). Access & sharing patches `publication_mode` /
 * authorized emails immediately on click or blur (same fields the
 * experience-v2 Share modal writes, so a value set here shows up preselected
 * there) — it never publishes anything itself.
 */
export const GeneralSettingsEditCard: React.FC<
  GeneralSettingsEditCardProps
> = ({ hasChatAddon }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const apiUrl = useApiUrl();
  const { mutateAsync } = useCustomMutation();
  const { mutateAsync: updateProject } = useUpdate<MediaProjectResponse>();
  const { currentOrganization } = useSession();
  const { projectId, instanceId } = useParams<{
    projectId: string;
    instanceId?: string;
  }>();
  // Mirrors ExperienceEditPage: the route's :instanceId is optional there,
  // and "default" is the instance it falls back to.
  const effectiveInstanceId = instanceId ?? "default";
  const [form] = Form.useForm<GeneralSettingsFormValues>();

  // `project` comes from here rather than a separate `useOne` — this hook
  // already fetches it (gated only on `projectId`, so it works for us even
  // though its own `instance`/`media` are gated on a `mediaId` we don't have
  // — see `instance` below for the fetch that actually covers that).
  const {
    project,
    media,
    markAsOutOfDate,
    invalidateProject,
    updateSettings,
    updateAccess,
    updateAuthorizedEmails,
  } = useProjectInstance({
    projectId: projectId ?? "",
    instanceId: effectiveInstanceId,
  });
  // No mode yet (loading) → session-only, so non-LTI users keep the skeleton.
  const isLti = isLtiExperience(media?.publication_mode);

  // The project's General add-on settings (goal, prompt, brand kit, voice,
  // side panel) — same read the edit page's rail uses, deduped by React Query.
  const { data: addOnsData } = useCustom<ProjectAddOnsResponse>({
    url: `${apiUrl}/media/projects/${projectId}/add-ons`,
    method: "get",
    queryOptions: { enabled: Boolean(projectId) },
  });
  const generalGeneration = addOnsData?.data.settings.general_generation_settings;
  const generalFunctional = addOnsData?.data.settings.general_functional_settings;
  // Content key, not object identity — same idea as `projectTagsKey`: a
  // refetch returning identical data must not re-fire the hydration effect
  // (its `setFieldsValue` would clobber an un-blurred prompt mid-typing).
  const generalSettingsKey = JSON.stringify([
    generalGeneration,
    generalFunctional,
  ]);

  const {
    data: instanceData,
    isLoading: isInstanceLoading,
    refetch: refetchInstance,
  } = useOne<MediaProjectInstanceResponse>({
    resource: `media/projects/${projectId}/instances`,
    id: effectiveInstanceId,
    queryOptions: { enabled: Boolean(projectId) },
  });
  const instance = instanceData?.data;

  // Same URL the experience-v2 Share modal shows — `AccessSharingSection`'s
  // Public card displays it read-only. Reads `media` (the project's own
  // media entry), not `instance` — `instance` comes from the `/instances`
  // endpoint, which is the live/published data and doesn't reflect a
  // settings PATCH until the next publish, leaving this stale (MED-6958).
  const shareUrl = getPublicationUrl({
    projectId: projectId ?? "",
    publicationId: effectiveInstanceId,
    instanceSlug: media?.instance_slug ?? undefined,
    organizationSlug: currentOrganization?.slug,
    experienceV2: true,
  });

  // Find the CTA asset the player actually uses — same discovery as
  // `LinksCtaSection`.
  const ctaAsset = useMemo(() => {
    const assets = instance?.assets as Record<string, MediaAsset> | undefined;
    if (!assets) return undefined;
    return Object.values(assets).find(
      (asset) => asset.asset_type === "CallToActions"
    );
  }, [instance?.assets]);

  // Tracks the CTA asset id outside React state, so an in-flight save sees it
  // immediately instead of waiting for `instance` to re-render (see `saveLinks`).
  const ctaAssetIdRef = useRef<string | undefined>(ctaAsset?.id);
  useEffect(() => {
    ctaAssetIdRef.current = ctaAsset?.id;
  }, [ctaAsset?.id]);

  const { saveCallToActionsAsset, fetchJsonAsset } = useMediaAssetsStorage({
    organizationId: currentOrganization?.id,
  });

  // Raw fetch result only — `null` while there's nothing to fetch or it
  // hasn't resolved yet. Kept separate from the derived `links` below so the
  // "no CTA asset" case needs no synchronous setState in the effect.
  const [fetchedActions, setFetchedActions] = useState<CallToAction[] | null>(
    null
  );
  useEffect(() => {
    if (!ctaAsset?.path) return;
    // Ignore this fetch's result if the path changes (or we unmount) before it resolves.
    const cancelled = { current: false };
    void (async () => {
      try {
        const json = (await fetchJsonAsset(ctaAsset.path)) as {
          actions?: CallToAction[];
        };
        if (!cancelled.current) setFetchedActions(json.actions ?? []);
      } catch {
        if (!cancelled.current) setFetchedActions([]);
      }
    })();
    return () => {
      cancelled.current = true;
    };
    // `fetchJsonAsset` deliberately excluded — it's a plain function on
    // `useMediaAssetsStorage` (not `useCallback`-wrapped), so a new reference
    // every render. Depending on it re-fires this fetch every render for as
    // long as `ctaAsset.path` is truthy: setFetchedActions → re-render → new
    // `fetchJsonAsset` → effect fires again → infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctaAsset?.path]);

  // Driven by `fetchedActions` alone (not gated on `ctaAsset?.path`) — after a
  // save, `saveLinks` updates `fetchedActions` synchronously, but `ctaAsset`
  // (derived from `instance.assets`) only catches up once the fire-and-forget
  // `refetchInstance()` resolves. Gating on it here would leave `links`/
  // `savedLinkIds` stale (and the just-created CTA unprotected by the
  // saved-link removal guard) for that whole window.
  const links = useMemo(
    () => mapCallToActionsToLinks(fetchedActions ?? []),
    [fetchedActions]
  );

  // CTAs this card can't edit (e.g. `DocumentDownload`) — merged back into
  // the save payload so it doesn't delete them (see `saveLinks`).
  const unmanagedActions = useMemo(
    () => getUnmanagedCallToActions(fetchedActions ?? []),
    [fetchedActions]
  );

  // Ids actually saved on the backend — lets `HeaderLinksSection` still
  // confirm removal of a row whose fields were cleared but never saved.
  const savedLinkIds = useMemo(
    () =>
      new Set(
        links.map((link) => link.id).filter((id): id is string => Boolean(id))
      ),
    [links]
  );

  // Backend truth — used by the blur handler and `saveLinks`'s no-op guard.
  const lastSavedLinksRef = useRef<ExperienceLink[]>(links);
  useEffect(() => {
    lastSavedLinksRef.current = links;
  }, [links]);
  // Form's last-synced snapshot, used by `isStructuralChange`.
  const lastFormLinksRef = useRef<ExperienceLink[]>(links);

  // Same idea for name/description — only these two need it since tags and
  // language save immediately (see `onValuesChange` below), not on blur.
  const lastSavedProjectFieldsRef = useRef<{
    name: string;
    description: string;
  }>({ name: "", description: "" });
  useEffect(() => {
    if (!project) return;
    lastSavedProjectFieldsRef.current = {
      name: project.title,
      description: project.description ?? "",
    };
  }, [project]);

  // Primitives, not the whole `project` object — keeps the effect below from
  // re-firing on every unrelated `markAsOutOfDate()` refetch.
  const projectTitle = project?.title;
  const projectDescription = project?.description ?? "";
  const projectTagsKey = project?.tags.map((tag) => tag.tag_value).join(",");
  // The document language is only detected at creation, so "Auto" stays
  // available only to a project still on it — never as a switch-back target.
  const isAutoLanguage =
    (generalGeneration?.language ??
      GENERAL_SETTINGS_DEFAULTS.generation_settings.language) === "auto";

  // What the backend has for the prompt — the blur handler's equality check.
  const lastSavedPromptRef = useRef<string>("");

  const isHydratingRef = useRef(false);
  useEffect(() => {
    if (!project) return;
    isHydratingRef.current = true;
    form.setFieldsValue({
      name: projectTitle,
      description: projectDescription,
      tags: project.tags.map((tag) => tag.tag_value),
      // Language shows the user's own General choice (often "auto"), not the
      // language the backend detected on the storyboard. API nulls (no stored
      // value) coalesce to undefined so the inputs show their placeholders
      // instead of antd rejecting the null.
      generation_settings: {
        objectives: generalGeneration?.objectives ?? undefined,
        generation_prompt: generalGeneration?.generation_prompt ?? undefined,
        brand_kit_id: generalGeneration?.brand_kit_id ?? undefined,
        contact_id: generalGeneration?.contact_id ?? undefined,
        language:
          generalGeneration?.language ??
          GENERAL_SETTINGS_DEFAULTS.generation_settings.language,
      },
      functional_settings: {
        side_panel_position:
          generalFunctional?.side_panel_position ?? undefined,
      },
      // `links` deliberately excluded — see `hasHydratedLinksRef` below.
    });
    lastSavedPromptRef.current = generalGeneration?.generation_prompt ?? "";
    isHydratingRef.current = false;
    // `project` deliberately excluded — see primitives above. The General
    // branches are keyed by content (`generalSettingsKey`), not identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectTitle,
    projectDescription,
    projectTagsKey,
    generalSettingsKey,
    form,
  ]);

  // Populates the form's `links` once, not on every recompute — otherwise a
  // post-save refetch would reset the whole list mid-edit (MED-7109).
  const hasHydratedLinksRef = useRef(false);
  useEffect(() => {
    if (hasHydratedLinksRef.current || isInstanceLoading) return;
    // Wait for the CTA JSON fetch to resolve, so we don't lock in an empty list.
    if (ctaAsset?.path && fetchedActions === null) return;
    isHydratingRef.current = true;
    form.setFieldValue("links", links);
    isHydratingRef.current = false;
    lastFormLinksRef.current = links;
    hasHydratedLinksRef.current = true;
  }, [isInstanceLoading, ctaAsset?.path, fetchedActions, links, form]);

  // `null` means nothing was actually persisted (no-op or a failed create) —
  // `commitLinks` must not advance `lastSavedLinksRef` in that case, or a
  // failed save gets recorded as saved and never retried. `true`/`false`
  // means a create/patch actually went through.
  const saveLinks = async (
    nextLinks: ExperienceLink[]
  ): Promise<boolean | null> => {
    if (!projectId) return null;
    // Merge in `unmanagedActions` so this full-replace PATCH doesn't delete them.
    const actions = [
      ...mapLinksToCallToActions(nextLinks),
      ...unmanagedActions,
    ];
    if (ctaAssetIdRef.current) {
      // Skip the patch if nothing content-bearing changed (e.g. an empty "Add CTA" row).
      const contentLinks = nextLinks.filter(hasLinkContent);
      if (JSON.stringify(contentLinks) === JSON.stringify(lastSavedLinksRef.current)) {
        return null;
      }
      // The group already exists — patch its content in place, same asset id.
      // BE already marks the publication out of date for that, so no
      // `markAsOutOfDate` needed here.
      await saveCallToActionsAsset({ actions }, ctaAssetIdRef.current);
      setFetchedActions(actions);
      void refetchInstance();
      return false;
    }
    if (actions.length === 0) return null;
    // First link ever added to this instance — create the group, then
    // point the instance at it.
    const assetId: string | undefined = await saveCallToActionsAsset(
      { actions },
      undefined,
      { category: "MediaMeta", description: "Experience CTAs" }
    );
    if (!assetId) return null;
    await mutateAsync({
      url: `${apiUrl}/media/projects/${projectId}/instances/${effectiveInstanceId}/settings`,
      method: "patch",
      values: {
        cta_group_id: assetId,
      } satisfies ProjectInstanceSettingsRequest,
      successNotification: false,
    });
    // Only set once the instance is actually linked — otherwise a failed
    // patch above would leave this pointing at an asset the instance never
    // reads, and every later save would silently patch that orphan instead
    // of retrying the create.
    ctaAssetIdRef.current = assetId;
    // We already know what's persisted — it's what we just sent — so update
    // locally instead of waiting on a refetch to tell us.
    setFetchedActions(actions);
    void refetchInstance();
    return true;
  };

  // Queues saves so a second one only starts once the first has fully
  // finished — otherwise both can see "no CTA asset yet" and both create one
  // (MED-7109).
  const linksSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  const commitLinks = (nextLinks: ExperienceLink[]) => {
    const queued = linksSaveQueueRef.current.catch(() => undefined).then(async () => {
      const created = await saveLinks(nextLinks);
      if (created === null) return;
      // What's actually in the backend now — not `nextLinks` itself, which may
      // still hold an incomplete draft row that `saveLinks` filtered out.
      lastSavedLinksRef.current = nextLinks.filter(hasLinkContent);
      // Only creation needs to flag out-of-date ourselves; BE handles the patch case.
      if (created) {
        await markAsOutOfDate();
      }
    });
    linksSaveQueueRef.current = queued;
    return queued;
  };

  // A different length or a changed `type` (the dropdown pick) is a discrete
  // action — save it right away, same as add/remove. Anything else is a
  // label/value edit still being typed, left to `handleLinksFieldBlur`.
  const isStructuralChange = (
    previous: ExperienceLink[],
    next: ExperienceLink[]
  ) =>
    previous.length !== next.length ||
    previous.some((link, index) => link.type !== next[index]?.type);

  const handleLinksFieldBlur = (rowIndex: number) => {
    const current =
      (form.getFieldValue("links") as ExperienceLink[] | undefined) ?? [];
    // Blurring the row's *own* fields isn't enough — a label typed with the
    // value still empty (or vice versa) can't be saved anyway (`hasLinkContent`),
    // and `saveLinks` would silently drop it, patching the asset for nothing.
    const row = current[rowIndex];
    if (!row || !hasLinkContent(row)) return;
    // Normalize this row's value the same way `saveLinks` will, so the form
    // and `lastSavedLinksRef` agree on what "saved" looks like — otherwise
    // this equality check never converges and every future blur re-saves.
    const normalizedValue = normalizeLinkValue(row.type, row.value);
    const next =
      normalizedValue === row.value
        ? current
        : current.map((link, i) =>
            i === rowIndex ? { ...link, value: normalizedValue } : link
          );
    if (next !== current) form.setFieldValue("links", next);
    if (JSON.stringify(next) === JSON.stringify(lastSavedLinksRef.current)) {
      return;
    }
    void commitLinks(next);
  };

  const commitProjectField = async (
    field: "name" | "description",
    value: string
  ) => {
    if (!projectId) return;
    await updateProject({
      resource: "media/projects",
      id: projectId,
      values: field === "name" ? { title: value } : { description: value },
      successNotification: false,
    });
    lastSavedProjectFieldsRef.current[field] = value;
    await markAsOutOfDate();
  };

  // The experience-wide generation settings live on the General add-on type.
  // Only the changed fields are sent: the backend applies just the fields set
  // in the payload, so resending hidden ones (language, prompt, objectives…)
  // would mark every add-on out of date. `add_on_type` tags the union. Not
  // `satisfies ProjectAddOnSettingsPatchRequest`: the generated type requires
  // `language` on the General branch, which a partial patch omits on purpose.
  const commitGeneralSettings = async (patch: {
    generation_settings?: Partial<
      GeneralSettingsFormValues["generation_settings"]
    >;
    functional_settings?: Partial<
      GeneralSettingsFormValues["functional_settings"]
    >;
  }) => {
    if (!projectId) return;
    await mutateAsync({
      url: `${apiUrl}/media/projects/${projectId}/add-ons/settings`,
      method: "patch",
      values: {
        add_on_type: "General",
        ...patch,
      },
      successNotification: false,
    });
    await markAsOutOfDate();
  };

  const handleDetailsFieldBlur = (field: ExperienceDetailsBlurField) => {
    // The prompt is a General setting, not a project field — same blur-commit
    // rhythm, its own destination and equality check.
    if (field === "generation_prompt") {
      const values = form.getFieldsValue(true) as GeneralSettingsFormValues;
      const prompt = values.generation_settings.generation_prompt ?? "";
      if (prompt === lastSavedPromptRef.current) return;
      lastSavedPromptRef.current = prompt;
      void commitGeneralSettings({
        generation_settings: { generation_prompt: prompt },
      });
      return;
    }
    const value = (
      (form.getFieldValue(field) as string | undefined) ?? ""
    ).trim();
    // Name can't be blanked out, but description is optional — clearing it
    // on purpose is a valid save, not a still-empty field to ignore.
    if (field === "name" && !value) return;
    if (value === lastSavedProjectFieldsRef.current[field]) return;
    void commitProjectField(field, value);
  };

  const commitTags = async (tags: string[]) => {
    if (!projectId) return;
    await updateProject({
      resource: "media/projects",
      id: projectId,
      values: { tags: tags.map((tag) => ({ tag_key: tag, tag_value: tag })) },
      successNotification: false,
    });
    await markAsOutOfDate();
  };


  // `markAsOutOfDate` invalidates the project itself, but only when it
  // actually fires (live instance, not already out of date) — so it can't be
  // relied on alone. `updateSettings`'s own invalidate is skipped instead and
  // done once here, after both calls, regardless of which one(s) did anything.
  // `refetchInstance` too — `invalidateProject` doesn't cover the separate
  // instances query this card reads the saved slug/access from.
  const markStaleAndRefresh = () =>
    Promise.all([markAsOutOfDate(), invalidateProject(), refetchInstance()]);

  // `SlugSection` only calls this once it's checked the slug itself and
  // confirmed it's available and different from the saved one.
  const commitSlug = async (newSlug: string) => {
    await updateSettings({ instance_slug: newSlug }, true);
    await markStaleAndRefresh();
  };

  // Same `publication_mode` field the experience-v2 Share modal writes on
  // its Save button — this only patches the setting, it never publishes.
  const commitAccess = async (
    access: "public" | "email",
    emails: string[] = []
  ) => {
    await updateAccess(access === "email", true);
    // → Email: the PATCH replaces the list, so send the first recipient(s).
    if (access === "email") {
      await updateAuthorizedEmails(emails);
    }
    await markStaleAndRefresh();
  };

  // Only called with a valid, confirmed password — never an empty one.
  const commitPassword = async (password: string) => {
    await updateSettings(
      { publication_mode: "PasswordProtected", password },
      true
    );
    await markStaleAndRefresh();
  };

  // `AccessSharingSection` always reports the full list (add/remove/CSV/blur
  // all resolve to one of these) — the emails PATCH replaces, not merges.
  const commitAuthorizedEmails = async (emails: string[]) => {
    await updateAuthorizedEmails(emails);
    await markAsOutOfDate();
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.General}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
    >
      <Form<GeneralSettingsFormValues>
        form={form}
        layout="vertical"
        initialValues={GENERAL_SETTINGS_DEFAULTS}
        onValuesChange={(changedValues, values) => {
          // Programmatic re-hydration, not a user edit — see `isHydratingRef`
          // above for why this guard exists.
          if (isHydratingRef.current) return;
          // Tags/language change on discrete select actions, not keystrokes —
          // safe to save immediately, unlike the text fields below.
          if ("tags" in changedValues) void commitTags(values.tags);
          if (changedValues.generation_settings?.language !== undefined) {
            void commitGeneralSettings({
              generation_settings: {
                language: values.generation_settings.language,
              },
            });
          }
          // The other generation fields are discrete selects — commit the
          // General branches right away. The prompt (continuous typing) is
          // left to `handleDetailsFieldBlur`.
          const gen = changedValues.generation_settings;
          if (
            gen?.objectives !== undefined ||
            gen?.contact_id !== undefined ||
            gen?.brand_kit_id !== undefined ||
            changedValues.functional_settings?.side_panel_position !== undefined
          ) {
            // Send only what changed — hidden fields are never resent.
            const sidePanelPosition =
              changedValues.functional_settings?.side_panel_position;
            void commitGeneralSettings({
              ...(gen
                ? {
                    generation_settings: {
                      ...(gen.objectives !== undefined && {
                        objectives: gen.objectives,
                      }),
                      ...(gen.contact_id !== undefined && {
                        contact_id: gen.contact_id,
                      }),
                      ...(gen.brand_kit_id !== undefined && {
                        brand_kit_id: gen.brand_kit_id,
                      }),
                    },
                  }
                : {}),
              ...(sidePanelPosition !== undefined && {
                functional_settings: { side_panel_position: sidePanelPosition },
              }),
            });
          }
          if ("links" in changedValues) {
            const next = values.links;
            const previous = lastFormLinksRef.current;
            // Add/remove/type-change: save immediately. A label/value edit is
            // left to `handleLinksFieldBlur` instead of saving per keystroke.
            if (isStructuralChange(previous, next)) {
              // A type change can flip whether a row's value needs
              // normalizing (see `normalizeLinkValue`) — apply it here too,
              // not just on blur, or a bare type change saves a raw value
              // that disagrees with what actually ends up stored.
              const normalized =
                previous.length === next.length
                  ? next.map((link, i) =>
                      link.type !== previous[i]?.type && hasLinkContent(link)
                        ? { ...link, value: normalizeLinkValue(link.type, link.value) }
                        : link
                    )
                  : next;
              if (normalized !== next) {
                isHydratingRef.current = true;
                form.setFieldValue("links", normalized);
                isHydratingRef.current = false;
              }
              void commitLinks(normalized);
              lastFormLinksRef.current = normalized;
            } else {
              lastFormLinksRef.current = next;
            }
          }
        }}
      >
        <ExperienceDetailsSection
          onFieldBlur={handleDetailsFieldBlur}
          includeAutoLanguage={isAutoLanguage}
          hiddenFields={HIDDEN_DETAILS_FIELDS}
        />

        <FieldBlock>
          <FormLabel label={t(`${KEY}.details.slug.label`)} />
          <SlugSection
            initialSlug={media?.instance_slug ?? undefined}
            title={project?.title}
            organizationSlug={currentOrganization?.slug}
            isLoading={isInstanceLoading}
            onCommit={(newSlug) => void commitSlug(newSlug)}
          />
        </FieldBlock>

        <SectionDivider $token={token} />

        <HeaderLinksSection
          confirmRemove
          onFieldBlur={handleLinksFieldBlur}
          savedLinkIds={savedLinkIds}
        />

        <SectionDivider $token={token} />

        <LayoutSection hasChatAddon={hasChatAddon} />

        {!isLti && (
          <>
            <SectionDivider $token={token} />

            <AccessSharingSection
              shareUrl={shareUrl}
              initialAccess={
                isInstanceLoading
                  ? undefined
                  : getShareAccess(media?.publication_mode)
              }
              onAccessChange={(access, emails) =>
                void commitAccess(access, emails)
              }
              onPasswordChange={(password) => void commitPassword(password)}
              onEmailsChange={(emails) => void commitAuthorizedEmails(emails)}
            />
          </>
        )}
      </Form>
    </PlaybookAddOnCard>
  );
};

export default GeneralSettingsEditCard;
